import Vue from 'vue';
import moment from 'moment';
import trainingOverviewData from './member/trainingOverviewData';
import { camelizeKeys } from '../lib/functions';
import MemberUtils from '../memberUtils';

export default {
  state: {
    loggedInMember: null,
    accessTokens: null,
    tokensUpdated: null,
    roster: null,
  },
  getters: {
    ...trainingOverviewData,
    loggedInMember: state => state.loggedInMember,
    prefersMetric: state => state.loggedInMember.prefersMetric,
    roster: state => state.roster,
    rosterMember: state => memberId => state.roster.find(m => m.memberId === memberId),
    tokensUpdated: state => state.tokensUpdated,
    isAuthenticated: state => () => state.accessTokens
      && (moment().utc().isBefore(moment(state.accessTokens.credentialsExpire).utc())),
    tokensExpired: state => () => state.accessTokens
      && moment().utc().isAfter(moment(state.accessTokens.credentialsExpire).utc()),
    tokens: state => state.accessTokens,
    hasRefreshTokens: state => (state.accessTokens
      && (state.accessTokens.idToken !== null)
      && (state.accessTokens.idToken !== undefined)
      && (state.accessTokens.refreshToken !== null)
      && (state.accessTokens.refreshToken !== undefined)
      && (state.accessTokens.accessToken !== null)
      && (state.accessTokens.accessToken !== undefined)),
  },
  mutations: {
    clearMember(state) {
      Vue.set(state, 'loggedInMember', null);
      Vue.set(state, 'roster', null);
      Vue.set(state, 'accessTokens', null);
      Vue.set(state, 'tokensUpdated', null);
    },
    loadMember(state, member) {
      Vue.set(state, 'loggedInMember', camelizeKeys(member));
    },
    updateMember(state, values) {
      Vue.set(state, 'loggedInMember', Object.assign(state.loggedInMember, values));
    },
    setAccessTokens(state, payload) {
      let data = camelizeKeys(payload);
      if (state.accessTokens) {
        data = Object.assign(state.accessTokens, data);
      }
      Vue.set(state, 'accessTokens', data);
      Vue.set(state, 'tokensUpdated', moment());
    },
    loadRoster(state, roster) {
      let data = roster.map(camelizeKeys);

      const member = this.getters.loggedInMember;
      if (this.getters.calendars[member.memberId].find(d => d.numActivities > 0)) {
        const m = JSON.parse(JSON.stringify(member));
        m.firstName = `* ${m.firstName}`;
        m.metrics = JSON.parse(JSON.stringify(this.getters.calendars[member.memberId]));
        data.push(m);
      }

      data = data.map((m) => {
        const ret = m;
        ret.metrics = m.metrics.map(d => ({
          ...d,
          date: moment(d.date),
        }));
        return ret;
      });
      Vue.set(state, 'roster', data);
    },
    expireTokens(state) {
      Vue.set(state.accessTokens, 'credentialsExpire', moment().subtract(1, 'minute').unix());
    },
  },
  actions: {
    async refreshMember(context) {
      if (!context.getters.loggedInMember) {
        return;
      }
      const response = await this.$container.dao.getMember(context.getters.loggedInMember.memberId);
      await context.dispatch('saveUser', response.data.member[0]);
    },
    async loadMember(context, memberId) {
      const response = await this.$container.dao.getMember(memberId);
      await context.dispatch('saveUser', response.data.member[0]);
    },
    async loadRoster(context) {
      const member = context.getters.loggedInMember;
      if (member === null) {
        console.warn('loadRoster called but there is no logged in member.');
        return;
      }

      const result = await this.$container.dao.getRoster(member.teams[0].teamId);
      context.commit('loadRoster', result.data);
    },
    async authenticate(context, { email, password }) {
      const data = { email, password };
      if (context.getters.isNative) {
        data.native = 1;
      }
      const result = await this.$container.apiHttpClient.post('/auth/authenticate', data);
      await context.commit('loadMember', result.data.member);
      await context.dispatch('saveUserAndTokens', result.data);

      return result.data;
    },
    async refreshToken(context) {
      if (!context.getters.loggedInMember || !context.getters.hasRefreshTokens) {
        return;
      }
      try {
        const result = await this.$container.dao.refreshToken(
          context.getters.loggedInMember.memberId,
          context.getters.tokens.idToken,
          context.getters.tokens.refreshToken,
          context.getters.tokens.accessToken,
        );
        await context.commit('setAccessTokens', result.data);
        await context.dispatch('refreshMember');
      } catch (e) {
        if (e.response.status === 401) {
          console.log(e);
          return;
        }
        console.error(e);
      }
    },
    async signOut(context) {
      context.commit('clearMember');
      context.commit('clearUser');
    },
    async register(context, { email, password, coupon = null }) {
      const data = { email, password };
      if (coupon) {
        data.coupon = coupon;
      }
      if (context.getters.isNative) {
        data.native = 1;
      }
      const result = await this.$container.apiHttpClient.post('/auth/register', data);
      await context.dispatch('saveUserAndTokens', result.data);

      return result.data;
    },
    saveUserAndTokens(context, data) {
      return Promise.all([
        context.dispatch('saveUser', data.member),
        context.commit('setAccessTokens', data.tokens),
      ]);
    },
    saveUser(context, member) {
      return Promise.all([
        context.commit('loadMember', member),
        // TODO when we remove the user module, we can remove this.
        context.dispatch('setUserProperties', MemberUtils.mapMember(member)),
      ]);
    },
  },
};
